// components/groups/TeamMembersCard.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { showError, showSuccess } from "toast/toast";
import { useNavigate, useLocation } from "react-router-dom";
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    createColumnHelper,
    Row,
} from "@tanstack/react-table";
import DataTable from "components/DataTable";
import PaginationControls from "components/PaginationControls";
import TablePaginationControls from "components/TableandmobilePaginationcontrol"
import { DesktopTableParts, MobileCardsOnly } from "components/TableandMobilecomponent";

import { getAuthCookie } from "../../utils/authCookies";

export type Agent = {
    user_id: string;
    name: string;
    email?: string;
    status?: string;
    license_type?: string;
    company_name?: string;
};

type Props = {
    groupId: string;
    members: Agent[];
    onMembersChanged: () => void;
    totalMembers?: number;
    onSearch: (term: string) => void;   // parent fetcher
    audience?: string; // "internal" or other values
};

const columnHelper = createColumnHelper<Agent>();

const TeamMembersCard: React.FC<Props> = ({ groupId, members, onMembersChanged, totalMembers, onSearch, audience }) => {
    // console.log("TeamMembersCard render", { groupId, members });

    const token = getAuthCookie("sessionToken");
    const userId = getAuthCookie("userId");
    const navigate = useNavigate();
    const location = useLocation();

    // Determine if we're in SignageTeamMember context (for dynamic signageFirmID)
    const isSignageTeammember = location.pathname.includes("/signageteammember");
    const [parentSignageId, setParentSignageId] = useState<string | null>(null);

    // Fetch parent hierarchy for SignageTeamMember login - get parent signage firm ID
    const fetchParentHierarchy = async () => {
        if (!isSignageTeammember || !token) return;
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/parent-hierarchy`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );
            if (!response.ok) return;
            const data = await response.json();
            const signageFirm = data.parent_chain?.find(
                (parent: any) => parent.license_type === "SIGNAGE_FIRM"
            );
            if (signageFirm) {
                setParentSignageId(signageFirm.id || signageFirm._id);
            }
        } catch (error) {
            console.error("Error fetching parent hierarchy:", error);
        }
    };

    useEffect(() => {
        if (isSignageTeammember && token) {
            fetchParentHierarchy();
        }
    }, [isSignageTeammember, token]);

    // Dynamic signageFirmID: use parent firm when SignageTeamMember, else userId
    const signageFirmID = (isSignageTeammember && parentSignageId ? parentSignageId : userId) || "";
    // ------------ table state ------------
    const [searchInput, setSearchInput] = useState("");

    // Reset pagination when data changes
    useEffect(() => {
        setPageIndex(0);
    }, [members]);

    // ------------ add-member modal state ------------
    const [showModal, setShowModal] = useState(false);
    /** Draft text in the modal search field (updates while typing). */
    const [modalQuery, setModalQuery] = useState("");
    /** Last search term sent to the API (Enter); used for infinite scroll + “select all”. */
    const [modalSearchCommitted, setModalSearchCommitted] = useState("");
    const [contacts, setContacts] = useState<
        { id: string; name: string; email: string }[]
    >([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [selected, setSelected] = useState<{ id: string; name: string; email: string }[]>([]);

    const listRef = useRef<HTMLDivElement>(null);
    const PAGE_SIZE = 30;
    const [isSelectingAll, setIsSelectingAll] = useState(false);

    type ContactItem = { id: string; name: string; email: string };

    // Fetch a single page - returns { data, hasMore } for use in loops
    const fetchPage = async (query: string, pageNumber: number): Promise<{ data: ContactItem[]; hasMore: boolean }> => {
        if (audience === "internal") {
            const params = new URLSearchParams({
                signageFirmID: signageFirmID,
                licenseType: "SIGNAGE_TEAM_MEMBER",
                page: String(pageNumber),
                limit: String(PAGE_SIZE)
            });
            const q = (query || "").trim();
            if (q) params.set("search", q);

            const url = `${process.env.REACT_APP_API_URL}/users-search?${params.toString()}`;
            const response = await fetch(url, {
                headers: {
                    Accept: "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                console.error("Failed to fetch team members:", response.statusText);
                return { data: [], hasMore: false };
            }

            const data = await response.json();
            const list = Array.isArray(data) ? data : data.data ?? data.results ?? [];
            const newData: ContactItem[] = list.map((u: any) => {
                const fullName = `${u?.firstName || ""} ${u?.lastName || ""}`.trim() || u?.text || "(no name)";
                return {
                    id: u?.id || u?._id || "",
                    name: fullName,
                    email: u?.email || "",
                };
            });

            const hasMore = Array.isArray(data)
                ? newData.length === PAGE_SIZE
                : data?.pagination?.more ??
                (data?.total ? pageNumber * PAGE_SIZE < data.total : newData.length === PAGE_SIZE);

            return { data: newData, hasMore };
        } else {
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/customer-groups/${userId}/users-created-or-linked?query=${encodeURIComponent(query)}&page=${pageNumber}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            const responseData = res.data?.data || res.data || [];
            const newData: ContactItem[] = responseData.map((u: any) => ({
                id: u.value || u.id || "",
                name: u.label?.split(" (")[0] || u.name || "",
                email: u.email || u.label?.match(/\((.*?)\)/)?.[1] || "",
            }));

            const total = res.data?.total || 0;
            const limit = res.data?.limit || PAGE_SIZE;
            const currentPage = res.data?.page || pageNumber;
            const totalPages = res.data?.totalPages || 1;
            const hasMore = currentPage < totalPages && newData.length >= limit;

            return { data: newData, hasMore };
        }
    };

    // fetch candidates for modal (infinite)
    const fetchUsers = async (query: string, pageNumber = 1) => {
        try {
            setIsLoading(true);
            const { data: newData, hasMore } = await fetchPage(query, pageNumber);
            if (pageNumber > 1) setContacts(prev => [...prev, ...newData]);
            else setContacts(newData);
            setHasMore(hasMore);
        } catch (e) {
            console.error("fetchUsers error", e);
        } finally {
            setIsLoading(false);
        }
    };

    const submitModalSearch = () => {
        const q = modalQuery.trim();
        setPage(1);
        setHasMore(true);
        setModalSearchCommitted(q);
        fetchUsers(q, 1);
    };

    const openModal = () => {
        setShowModal(true);
        setContacts([]);
        setSelected([]);
        setModalQuery("");
        setModalSearchCommitted("");
        setPage(1);
        setHasMore(true);
        fetchUsers("", 1);
    };

    const onListScroll = () => {
        const el = listRef.current;
        if (!el || isLoading || !hasMore) return;
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 10) {
            const next = page + 1;
            setPage(next);
            fetchUsers(modalSearchCommitted, next);
        }
    };

    const toggleSelect = (c: { id: string; name: string; email: string }) => {
        setSelected(prev =>
            prev.find(x => x.id === c.id) ? prev.filter(x => x.id !== c.id) : [...prev, c]
        );
    };

    const removeChip = (id: string) => setSelected(prev => prev.filter(x => x.id !== id));

    const selectAllContacts = async () => {
        const allCurrentlySelected = contacts.length > 0 && contacts.every(c => selected.some(s => s.id === c.id));
        if (allCurrentlySelected) {
            const contactIds = new Set(contacts.map(c => c.id));
            setSelected(prev => prev.filter(s => !contactIds.has(s.id)));
            return;
        }

        try {
            setIsSelectingAll(true);
            const allContacts: ContactItem[] = [];
            let pageNum = 1;
            let hasMorePages = true;

            while (hasMorePages) {
                const { data, hasMore } = await fetchPage(modalSearchCommitted, pageNum);
                allContacts.push(...data);
                hasMorePages = hasMore;
                pageNum++;
            }

            setContacts(allContacts);
            setPage(pageNum);
            setHasMore(false);

            const ids = new Set(selected.map(s => s.id));
            const toAdd = allContacts.filter(c => !ids.has(c.id));
            setSelected(prev => [...prev, ...toAdd]);
        } catch (e) {
            console.error("selectAllContacts error", e);
            showError("Failed to load all contacts.");
        } finally {
            setIsSelectingAll(false);
        }
    };

    const addMembersBulk = async () => {
        if (!selected.length) return;
        try {
            // Extract all user IDs from selected users
            const response = await fetch(`${process.env.REACT_APP_API_URL}/customer-groups/${groupId}/users`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify({ user_ids: selected.map(s => s.id) }), // Changed from user_id to user_ids array
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || "Failed to add members");
            }

            showSuccess("Added to the group successfully.");
            setShowModal(false);
            onMembersChanged();
        } catch (e) {
            console.error(e);
            showError("Failed to add to the group.");
        }
    };

    // ------------ table columns ------------
    const columns = useMemo(
        () => [
            // # (Sr. no) — purple
            columnHelper.display({
                id: "sn",
                header: () => <div className="th-text text-center">#</div>,
                cell: ({ row, table }) => {
                    const p = table.getState().pagination.pageIndex;
                    const s = table.getState().pagination.pageSize;
                    // Get the actual row position in the paginated data
                    const paginatedRows = table.getPaginationRowModel().rows;
                    const actualRowIndex = paginatedRows.findIndex(r => r.id === row.id);
                    const serialNumber = p * s + actualRowIndex + 1;
                    return <div className="text-sm tm-serial text-center">{serialNumber}</div>;
                },
                enableSorting: false,
                size: 60,
            }),

            // Member (name + email stacked)
            columnHelper.display({
                id: "member",
                header: () => <div className="th-text">Member</div>,
                cell: ({ row }) => {
                    const displayName =
                        row.original.license_type === "BROKERAGE_FIRM"
                            ? row.original.company_name || row.original.name
                            : row.original.name;

                    return (
                        <div className="d-flex flex-column">
                            <span className="tm-member-name">{displayName || "—"}</span>
                            <span className="tm-member-email small">{row.original.email || "—"}</span>
                        </div>
                    );
                },
                size: 360,
            }),

            // Role pill (defaults to "Agent" if your API doesn't send a role yet)
            columnHelper.display({
                id: "role",
                header: () => <div className="th-text">Role</div>,
                cell: ({ row }) => {
                    const role = (row.original as any).role || "---";
                    return <span className="tm-role">{role}</span>;
                },
                size: 160,
            }),

            // Status (dot + label like the ref)
            columnHelper.display({
                id: "status",
                header: () => <div className="th-text text-lg-center">Status</div>,
                cell: ({ row }) => {
                    const isActive = String(row.original.status || "").toLowerCase() === "active";
                    return (
                        <div className="tm-status justify-content-lg-center">
                            <span className={`tm-dot ${isActive ? "tm-dot-active" : "tm-dot-inactive"}`} />
                            <span className={isActive ? "fw-medium" : "text-muted"} style={isActive ? { color: "#008236" } : undefined}>
                                {isActive ? "Active" : "Inactive"}
                            </span>
                        </div>
                    );
                },
                size: 160,
            }),

            // Actions (unchanged)
            columnHelper.display({
                id: "actions",
                header: () => <div className="th-text text-center">Actions</div>,
                cell: ({ row }) => (
                    <div className="text-sm text-center">
                        <button
                            className="border-0 p-0"
                            title="Delete"
                            onClick={() =>
                                window.confirm("Are you sure you want to delete this agent?") &&
                                removeMember(row.original.user_id)
                            }
                        >
                            <i className="ri-delete-bin-line text-danger fs-18" />
                        </button>
                    </div>
                ),
                enableSorting: false,
                size: 120,
            }),
        ],
        []
    );

    // delete
    const removeMember = async (userId: string) => {
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/customer-groups/${groupId}/users/${userId}`, {
                method: "DELETE",
                headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            });
            if (!res.ok) throw new Error();
            showSuccess("Agent deleted successfully.");
            onMembersChanged();
        } catch {
            showError("Failed to delete agent.");
        }
    };

    // table instance
    const [pageSize, setPageSize] = useState(10);
    const [pageIndex, setPageIndex] = useState(0);
    const table = useReactTable({
        data: members, // Use members directly from parent
        columns,
        state: { pagination: { pageIndex, pageSize } },
        onPaginationChange: updater => {
            const next = typeof updater === "function" ? updater({ pageIndex, pageSize }) : updater;
            setPageIndex(next.pageIndex);
            setPageSize(next.pageSize);
        },
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        manualPagination: false,
        // Ensure proper pagination
        pageCount: Math.ceil(members.length / pageSize),
    });

    const addLabel = `Add${selected.length ? ` (${selected.length})` : ""} Members`;

    return (
        <div className="card border-0 shadow rounded-4 tm-card">
            {/* scoped styles */}
            <style>{`
            .po-search{border: 1.5px solid #d1d5dc;}
            .tm-modal .section-title{
                font-weight: 600;
                color: #64748b;
                font-size: .875rem;
            }
        .tm-card .tm-header{ background:#4832e4; border-radius:1rem 1rem 0 0; padding:.75rem 1rem; }
        .tm-card .tm-chip{ width:34px;height:34px;border-radius:10px;background:rgba(255,255,255,.15);color:#fff;
                           display:flex;align-items:center;justify-content:center; }
        .tm-card .tm-title{ color:#fff; font-weight:700; }
        .tm-card .tm-add{ background:#6b5df2; color:#fff; border:none; }
        .tm-card .tm-add:hover{ background:#7a6df7; color:#fff; }
        .tm-card .tm-toolbar .badge{ border-radius:9999px; }
        /* Modal */
        .tm-modal .modal-dialog{ max-width:720px; }
        .tm-modal .modal-content{ border-radius:16px; }
        .tm-modal .chip{ background:#eef2ff; color:#2f6cf5; border:1px solid rgba(47,108,245,.2); 
                         border-radius:9999px; padding:.25rem .6rem; }
        .tm-modal .list{ max-height:320px; overflow:auto; border:1px solid #e9ecef; border-radius:.75rem; }
        .tm-modal .selected-chips{ max-height:140px; overflow-y:auto; overflow-x:hidden; }
        .tm-modal .row-item{ padding:.5rem .75rem; }
        .tm-modal .row-item:hover{ background:#f8f9ff; }
        .tm-modal .tm-select-all{ background:#f0f4ff; border-bottom:1px solid #e9ecef; }
        .tm-modal .tm-select-all:hover{ background:#e8eeff; }
        .tm-modal .tm-select-all-loading{ opacity:.85; }
        .tm-modal .tm-select-all input:disabled{ cursor:not-allowed; }
        .tm-modal .btn-primary{ background:#2f6cf5; border-color:#2f6cf5; }
        .tm-modal .btn-primary:hover{ background:#215ae8; border-color:#215ae8; }
        /* table tweaks */
        .tm-serial{ color:#4f39f6; font-weight:600; }
        .tm-member-name{ font-weight:600; }
        .tm-member-email{ color:#64748b; }
        .tm-role{
          color:#8200db; background:#faf5ff; border:1px solid #e9d4ff;
          padding:.25rem .6rem; border-radius:9999px; font-weight:600; font-size:.875rem;
        }
        .tm-status{ display:flex; align-items:center; gap:.4rem; }
        .tm-dot{ width:8px; height:8px; border-radius:50%; display:inline-block; }
        .tm-dot-active{ background:#22c55e; }    /* green */
        .tm-dot-inactive{ background:#94a3b8; }  /* gray */
        /* Header gradient: light blue -> lilac */
        .tm-card table thead tr{
          background-image: linear-gradient(90deg, #eef2ff 0%, #f9f5ff 100%);
        }

        /* Make header cells transparent so the row gradient shows through */
        .tm-card table thead th{
          background: transparent !important;
          border-bottom: 1px solid #e9ecef;
          color: #0f172a;          /* dark header text */
          font-weight: 600;
        }
          /* --- Responsive modal sizing --- */
          /* Smaller, responsive modal — single source of truth */
        .tm-modal .modal-dialog{
          /* Bootstrap 5 way to size a modal */
          --bs-modal-width: 560px;           /* desktop width */
        }
        .tm-modal .modal-content{
          max-height: 99%;                  /* overall height */
          display: flex;
          flex-direction: column;
        }
        .tm-modal .modal-header,
        .tm-modal .modal-footer{ flex-shrink: 0; }

        /* body flex container; allow child to actually scroll */
        .tm-modal .modal-body{
          overflow: hidden;
          display: flex;
          flex-direction: column;
          gap: 12px;
          min-height: 0;                     /* <-- enables child scrolling */
        }

        /* the scrollable area */
        .tm-modal .list{
          flex: 1 1 auto;
          overflow: auto;                    /* vertical scrollbar appears here */
          border: 1px solid #e9ecef;
          border-radius: .75rem;
        }

        /* phones */
        @media (max-width: 576px){
          .tm-modal .modal-dialog{ --bs-modal-width: 95vw; }
          .tm-modal .modal-content{ max-height: 85vh; left:0 !important;}
        }

        /* Center the Bootstrap modal on all screens */
        .tm-modal.show.d-block{
          display: flex !important;        /* override Bootstrap's block */
          align-items: center;             /* vertical center */
          justify-content: center;         /* horizontal center */
          min-height: 100vh;               /* full-viewport flex container */
        }
                    
        /* Remove default top/bottom margins so centering is exact */
        .tm-modal .modal-dialog{
          margin: 0;                       /* center handled by flex above */
        }
                    
        /* Safety: very short viewports (tiny laptops / split-screen) */
        @media (max-height: 520px){
          .tm-modal.show.d-block{ align-items: flex-start; }
          .tm-modal .modal-dialog{ margin-top: .75rem; }
        }
                    
        /* MOBILE: narrower, centered modal */
        @media (max-width: 576px){
          /* center the whole overlay and give a little breathing room */
          .tm-modal.show.d-block{
            padding: 12px;                 /* keeps dialog off the screen edges */
            display: flex !important;
            align-items: center;
            justify-content: center;
            min-height: 100vh;
          }
                    
          /* shrink width on phones (between 320px and 420px, ~88vw target) */
          .tm-modal .modal-dialog{
            --bs-modal-width: clamp(320px, 88vw, 420px);
            margin: 0;                     /* centering handled by the flex parent */
          }
                    
          /* slightly shorter height on phones */
          .tm-modal .modal-content{
            max-height: 82vh;
            border-radius: 12px;
          }
        }

      `}</style>

            {/* Header */}
            <div className="card-header tm-header rounded-top-4 d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-2">
                    <span className="tm-chip"><i className="ri-shield-user-line" /></span>
                    <h5 className="mb-0 tm-title">Group Members</h5>
                </div>
                <button className="br-8 btn btn-sm fs-13 fw-medium tm-add" onClick={openModal}>
                    <i className="ri-user-add-line me-1" /> Add Member
                </button>
            </div>

            {/* Body */}
            <div className="card-body">
                {/* Toolbar */}
                <div className="tm-toolbar d-flex flex-wrap align-items-center gap-2 mb-3">
                    {/* <div className="input-group" style={{ width: 320, height: 40 }}>
            <span className="input-group-text bg-white border-end-0 p-2">
              <i className="ri-search-line fs-15" />
            </span>
            <input
              className="form-control border-start-0 ps-0"
              placeholder="Search members by name, email or role..."
              value={searchInput}
              onChange={e => table.setGlobalFilter(e.target.value)}
            />
          </div> */}
                    <div className="input-group po-search shadow-lg">
                        <span className="input-group-text"><i className="ri-search-line" /></span>
                        <input
                            className="form-control"
                            placeholder="Search members by name, email or role"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    onSearch(searchInput.trim());
                                }
                            }}
                        />
                    </div>



                    {/* <span className="align-items-center bg-gray-50 br-8 d-flex gap-1 px-4 py-2 text-body">
                        <i className="ri-eye-line" />
                        Showing all {members.length} members
                    </span> */}
                </div>

                <hr className="mb-2" style={{ borderTop: "none" }} />
                {/* DESKTOP (≥ 1022px) */}
                <div className="rdt-desktop shadow-sm">
                    <div
                        className="production-scroll"
                        style={{ "--rdt-min": "1200px" } as React.CSSProperties}
                    >
                        <table className="table production-table align-middle mb-0 table-1">
                            <DesktopTableParts table={table} />
                        </table>
                    </div>
                </div>

                {/* MOBILE (≤ 1021px) */}
                <div className="rdt-mobile">
                    <MobileCardsOnly
                        table={table}
                        idColId="order_link"
                        titleColId="product_name"
                        actionColId="actions"
                    />
                </div>

                {/* Pagination / Empty */}
                {table.getRowModel().rows.length === 0 ? (
                    <div className="text-center my-4 text-muted" style={{ fontSize: "1.1rem" }}>
                        No records found
                    </div>
                ) : (
                    <div className="mt-4 mb-4">
                        <TablePaginationControls table={table} totalItems={totalMembers} />
                    </div>
                )}
                <div className="row">
                    <div className="col-md-12">
                        <button type="button" className="btn back-btn surface-btn fw-bold fs-16" onClick={() => navigate(-1)}><i className="bi bi-chevron-left me-2"></i>Back</button>
                    </div>
                </div>
            </div>

            {/* Add Members Modal */}
            <div className={`modal fade tm-modal ${showModal ? "show d-block" : ""}`} tabIndex={-1} role="dialog" aria-modal="true">
                <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
                    <div className="modal-content">
                        {/* Modal header */}
                        <div className="modal-header">
                            <h5 className="modal-title d-flex align-items-center gap-2">
                                <i className="ri-user-add-line" style={{ color: "#4f39f6" }}></i> Add Members to Group
                            </h5>
                            <button type="button" className="btn-close" aria-label="Close" onClick={() => setShowModal(false)}></button>
                        </div>

                        {/* Modal body */}
                        <div className="modal-body">
                            <div className="text-muted mb-3">
                                Search and select existing contacts to add to the Sales Tax Cobb and Alpharetta group.
                            </div>

                            {/* Search inside modal */}
                            <div className="input-group mb-3" style={{ height: 40 }}>
                                <span className="input-group-text bg-white border-end-0 p-2">
                                    <i className="ri-search-line fs-15" />
                                </span>
                                <input
                                    type="text"
                                    name="addMembersContactSearch"
                                    className="form-control border-start-0 ps-0"
                                    placeholder="Search contacts by name, email, role or department..."
                                    value={modalQuery}
                                    autoComplete="off"
                                    autoCorrect="off"
                                    autoCapitalize="off"
                                    spellCheck={false}
                                    onChange={(e) => setModalQuery(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault();
                                            submitModalSearch();
                                        }
                                    }}
                                />
                            </div>

                            {/* NEW: Selected (N) header + Clear all */}
                            {selected.length > 0 && (
                                <div className="d-flex align-items-center justify-content-between mb-2">
                                    <span className="section-title">Selected ({selected.length})</span>
                                    <button type="button" className="btn btn-link p-0 small" onClick={() => setSelected([])}>
                                        Clear all
                                    </button>
                                </div>
                            )}

                            {/* Selected chips */}
                            {selected.length > 0 && (
                                <div className="d-flex flex-wrap gap-2 selected-chips">
                                    {selected.map(s => (
                                        <span key={s.id} className="chip d-inline-flex align-items-center gap-2">
                                            {s.name}
                                            <button className="btn btn-sm p-0 border-0" onClick={() => removeChip(s.id)}>
                                                <i className="ri-close-line"></i>
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            )}

                            {/* NEW: Available Contacts (M) header */}
                            <div className="d-flex align-items-center justify-content-between mb-2">
                                <span className="section-title">
                                    Available Contacts ({contacts.length}{hasMore ? "+" : ""})
                                </span>
                            </div>

                            {/* List */}
                            <div className="list" ref={listRef} onScroll={onListScroll}>
                                {(contacts.length > 0 || isSelectingAll) && (
                                    <label className={`d-flex align-items-center justify-content-between row-item tm-select-all ${isSelectingAll ? "tm-select-all-loading" : ""}`}>
                                        <div className="d-flex align-items-center gap-3">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                checked={contacts.length > 0 && contacts.every(c => selected.some(s => s.id === c.id))}
                                                onChange={selectAllContacts}
                                                disabled={isSelectingAll}
                                            />
                                            <div className="fw-semibold">
                                                All {isSelectingAll && <span className="text-muted small">(loading all…)</span>}
                                            </div>
                                        </div>
                                    </label>
                                )}
                                {contacts.map(c => {
                                    const checked = !!selected.find(x => x.id === c.id);
                                    return (
                                        <label key={c.id} className="d-flex align-items-center justify-content-between row-item">
                                            <div className="d-flex align-items-center gap-3">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={checked}
                                                    onChange={() => toggleSelect(c)}
                                                />
                                                <div>
                                                    <div className="fw-semibold">{c.name}</div>
                                                    <div className="text-muted small">{c.email}</div>
                                                </div>
                                            </div>
                                        </label>
                                    );
                                })}
                                {isLoading && <div className="text-center py-3 text-muted small">Loading…</div>}
                                {!isLoading && contacts.length === 0 && (
                                    <div className="text-center py-3 text-muted small">No contacts found</div>
                                )}
                            </div>
                        </div>

                        {/* Modal footer */}
                        <div className="modal-footer">
                            <button className="btn btn-light" onClick={() => setShowModal(false)}>
                                Cancel
                            </button>
                            <button className="btn btn-primary" disabled={!selected.length} onClick={addMembersBulk}>
                                <i className="ri-user-add-line me-1" />
                                Add {selected.length ? `(${selected.length}) ` : ""}Members
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {showModal && <div className="modal-backdrop fade show"></div>}

        </div>
    );
};

export default TeamMembersCard;
